import { createLogic } from "redux-logic";
import * as DOMUtils from "history/DOMUtils";
import type { BuildAndBuyLogicType } from "../store";
import { addNotification } from "../../../../../common-deprecated/redux/actions/NotificationActions";
import type { LeasingFinanceResponseType } from "../actions/BusinessFinanceActions";
import {
    businessLeasingErrorFinanceCalculation,
    businessFinishFinanceCalculation,
    businessSetFinanceInput,
    businessStartFinanceCalculation,
    businessStartFinanceRateFetch,
    businessClearFinanceRate,
    businessFinishFinanceRateFetch,
    businessErrorFinanceRateFetch,
} from "../actions/BusinessFinanceActions";
import { getBnbLabel } from "../../utils/labels";
import { getBusinessFinanceStorageKey, getBusinessLeasingFinanceConfigRequestBody } from "../../utils/selectorUtils";
import Debug from "../../../../../common-deprecated/Debug";
import { fetchB2bMonthlyRate } from "../../utils/FinanceInsuranceRate";
import { updateQuoteConfig } from "../actions/FinanceInsuranceActions";
import type { UpdateQuoteConfigType } from "../../../../../common-deprecated/types/CommonTypes";
import { extractCalculatorFromFinanceRateFetch } from "../../utils/b2bUtils";
import { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";

type FinanceCalculationTriggerActions = typeof businessSetFinanceInput;
const triggerFinanceCalculationLogic = (
    createLogic as BuildAndBuyLogicType<ReturnType<FinanceCalculationTriggerActions>>
)({
    type: [businessSetFinanceInput.type],
    process(store, dispatch, done) {
        dispatch(businessStartFinanceCalculation());
        done();
    },
});

const fetchFinanceCalculationLogic = (
    createLogic as BuildAndBuyLogicType<ReturnType<typeof businessStartFinanceCalculation>>
)({
    type: businessStartFinanceCalculation.type,
    name: "FetchFinanceCalculationLogic",
    validate({ getState, action }, allow, reject) {
        // Only allow finance loading when the we're not loading yet.
        if (getState().finance.businessCalculator.loading) {
            (reject as () => void)();
        } else {
            allow(action);
        }
    },

    async process({ getState }, dispatch, done) {
        try {
            const state = getState();
            const { isB2B } = state.commonSettings.query;
            if (!isB2B) done();

            const url = `${state.bnbSettings.businessLeasingEndpoint}/detail/finance`;

            const body = getBusinessLeasingFinanceConfigRequestBody(state);

            const fetchResult = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                referrer: "no-referrer",
                body: JSON.stringify(body),
            });
            if (fetchResult.status === 200) {
                const financeResult: LeasingFinanceResponseType = await fetchResult.json();
                dispatch(businessFinishFinanceCalculation(financeResult));
            } else {
                dispatch(businessLeasingErrorFinanceCalculation());
            }
        } catch (e) {
            dispatch(businessLeasingErrorFinanceCalculation());
        } finally {
            done();
        }
    },
});

const finishBusinessFinanceFetch = (
    createLogic as BuildAndBuyLogicType<ReturnType<typeof businessFinishFinanceRateFetch>>
)({
    type: businessFinishFinanceRateFetch.type,
    name: "FinishFetchFinanceRateLogic",
    transform({ getState, action }, next, reject) {
        if (!DOMUtils.canUseDOM) (reject as () => void)();
        const { finance } = getState();
        const newRates = { ...finance.itemRates };

        action.payload.rates = newRates;
        action.payload.previousDownPayment = finance.quoteInfo.DOWNPAYMENT
            ? Number(finance.quoteInfo.DOWNPAYMENT.value)
            : 0;

        const { quoteInfo } = action.payload.result;
        if (Object.keys(quoteInfo || {}).length === 0) {
            Debug.warn("ccGetMonthlyPayment returned with empty response, show No price available.");
            reject(businessErrorFinanceRateFetch());
        } else {
            const calculation = extractCalculatorFromFinanceRateFetch(action.payload.result);
            action.payload.calculation = calculation;
            next(action);
        }
    },
    async process({ getState, action }, dispatch, done) {
        const state = getState();
        const { bnbSettings, commonSettings } = state;
        const { result, trigger } = action.payload;
        const { quoteInfo } = result;

        if (quoteInfo) {
            sessionStorage.setItem(
                getBusinessFinanceStorageKey(state),
                JSON.stringify({
                    ...state.finance.quotes,
                    finance: quoteInfo.FINANCEQUOTEID.value,
                }),
            );

            dispatch(updateQuoteConfig({ finance: quoteInfo.FINANCEQUOTEID.value }, false));

            if (trigger === FinanceUpdateTrigger.NewConfig) {
                const [financeNotificationTitle, financeNotificationDescription] = getBnbLabel(
                    { commonSettings, bnbSettings },
                    ["financeNotificationTitle", "financeNotificationDescription"],
                );
                dispatch(
                    addNotification([
                        {
                            title: financeNotificationTitle,
                            description: financeNotificationDescription,
                        },
                    ]),
                );
            }
        }
        done();
    },
});

const startBusinessFinanceFetch = (
    createLogic as BuildAndBuyLogicType<ReturnType<typeof businessStartFinanceRateFetch>>
)({
    type: businessStartFinanceRateFetch.type,
    name: "FetchFinanceRateLogic",
    latest: true,
    validate({ action, getState }, allow, reject) {
        const { fetchAndParseJson } = action.payload;
        const { psls, bnbSettings } = getState();
        if (
            (!DOMUtils.canUseDOM && !fetchAndParseJson) ||
            (bnbSettings.addPSLSToFinanceConfigurationObject && psls.fetching)
        ) {
            (reject as () => void)();
        } else {
            allow(action);
        }
    },
    async process({ getState, action }, dispatch, done) {
        try {
            const state = getState();
            const { commonSettings, bnbSettings } = state;
            const { isB2B } = commonSettings.query;
            if (!isB2B) done();
            const { finance } = JSON.parse(
                sessionStorage.getItem(getBusinessFinanceStorageKey(state)) || "{}",
            ) as UpdateQuoteConfigType;
            const body = getBusinessLeasingFinanceConfigRequestBody(state);
            body.Parameters = { ...body.Parameters, FinanceQuoteID: finance || undefined };

            const result = await fetchB2bMonthlyRate(commonSettings, bnbSettings, body);
            dispatch(businessFinishFinanceRateFetch({ result, trigger: action.payload.trigger }));
        } catch (e) {
            dispatch(businessClearFinanceRate());
            Debug.error("Failed to fetch business monthly rate", e);
        } finally {
            done();
        }
    },
});

export default [
    triggerFinanceCalculationLogic,
    fetchFinanceCalculationLogic,
    startBusinessFinanceFetch,
    finishBusinessFinanceFetch,
];
